// Extracted verbatim from views/CaseDetail.js (PART C). Imports are the union
// used across the case-detail tabs; unused ones in a given tab are harmless.
import React, { useEffect, useRef, useState } from "react";
import CenteredModal from "../../components/CenteredModal";
import Spinner from "../../components/Spinner";
import Confirm from "../../components/Confirm";
import Modal from "../../components/Modal";
import { TipoPill, EstadoPill, SlaPill, TipoFalloPill, ESTADO_LABEL, PREFER_SUB_TIPO_STATES } from "../../components/Pills";
import { formatTipo } from "../../utils/case_taxonomy";
import { cases, insumos as insumosApi, users as usersApi, moderation as moderationApi } from "../../api/endpoints";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import InsumosTab from "../insumos/InsumosTab";
import LitisoftFields from "../../components/LitisoftFields";
import LitisoftIncompleteModal from "../../components/LitisoftIncompleteModal";
import LitisoftMandatoryInline from "../../components/LitisoftMandatoryInline";
import { TERMINO_OPTIONS, formatTermino } from "../../utils/termino";
import { canMarkContestadaDirectly, canRequestModeration, availableContestadaCategorias, CONTESTADA_LABELS as CONTESTADA_LABELS_FULL, rolesOf } from "../../utils/permissions";
import LawyerActionPanel from "../../components/lawyer/LawyerActionPanel";
import ProcesoCarpetasBanner from "../../components/ProcesoCarpetasBanner";
import CarpetaDocumentsPanel from "../../components/CarpetaDocumentsPanel";
import CarpetaStatusBadge from "../../components/CarpetaStatusBadge";
import InlineEditAccionante from "../../components/InlineEditAccionante";
import TipoFalloEditModal from "../../components/TipoFalloEditModal";
import { PERSONA_EDIT_ROLES } from "../Personas";
import { fmt, InlineEdit, hasLawyerRole, MarcarContestada, CaseFlagsToggles, hasSancionesSpecialization, SancionLifecycle, MarcarInsumoDeficiente, SolicitarModeracion, LawyerAgencyButtons, TABS, CONTESTADA_LABELS, TipoActuacionDisplay } from "./shared";

export default function GeneralTab({ c, user, onChanged, onClose }) {
  const toast = useToast();
  const [reassigning, setReassigning] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [chosen, setChosen] = useState(c.abogado_asignado_id || "");
  const [confirmFlag, setConfirmFlag] = useState(false);
  const [flagMotivo, setFlagMotivo] = useState("");
  const [confirmSoftDelete, setConfirmSoftDelete] = useState(false);
  // feature/admin-send-back. Admin/directora can revert
  // pendiente_archivar / archivado back to asignado_abogado when the
  // lawyer or Reparto closed something by mistake.
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackReason, setSendBackReason] = useState("");
  const [sendBackBusy, setSendBackBusy] = useState(false);
  // feature/single-role-audit — multi-role aware.
  const _gtRoles = rolesOf(user);
  const isReparto = _gtRoles.includes("admin") || _gtRoles.includes("reparto");
  const isDirectora = _gtRoles.includes("directora") || _gtRoles.includes("admin");
  const isAbogadoAssigned = _gtRoles.includes("abogado") && c.abogado_asignado_id === user.id;
  // feature/lawyer-assignment-by-reparto-insumos. Targeted helper
  // for the "Reasignar / Asignar abogado" affordance: admin, directora,
  // reparto, AND reparto_insumos (Camilo). Multi-role aware.
  const userRolesForAssignment = (user?.roles && user.roles.length) ? user.roles : (user?.role ? [user.role] : []);
  const canAssignLawyer = ["admin", "directora", "reparto", "reparto_insumos"].some((r) => userRolesForAssignment.includes(r));
  // feature/depth-and-delete — soft-delete permission: admin / reparto / directora / creator.
  // feature/single-role-audit — multi-role aware.
  const canSoftDelete = ["admin","reparto","directora"].some(r => _gtRoles.includes(r))
                     || c.created_by_id === user.id;
  const canFlagError = isReparto && c.estado !== "error_marcado";
  // feature/admin-send-back. Only admin/directora, only on
  // already-archived states. Multi-role aware.
  const canSendBack = ["admin", "directora"].some((r) => userRolesForAssignment.includes(r))
                   && (c.estado === "pendiente_archivar" || c.estado === "archivado");
  // feature/desfavorable-parallel-decision. Replaces
  // window.prompt() in the impugnar banner with a CenteredModal so the
  // "no impugnar" motivo flow matches the rest of the platform's UX
  // (frosted glass, focus trap, cancellable, no native browser chrome).
  const [noImpugnarOpen, setNoImpugnarOpen] = useState(false);
  const [noImpugnarReason, setNoImpugnarReason] = useState("");
  const [noImpugnarBusy, setNoImpugnarBusy] = useState(false);
  async function submitNoImpugnar() {
    const motivo = noImpugnarReason.trim();
    if (!motivo) {
      toast.push("Indique el motivo para no impugnar", "error");
      return;
    }
    setNoImpugnarBusy(true);
    try {
      await cases.action(c.id, "decide_impugnar", { impugnar: false, por_que_no_impugno: motivo });
      toast.push("Decisión: No impugnar registrada");
      setNoImpugnarOpen(false);
      setNoImpugnarReason("");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.message || e?.response?.data?.error || "Error al registrar la decisión", "error");
    } finally {
      setNoImpugnarBusy(false);
    }
  }

  async function saveField(field, value) {
    try {
      await cases.update(c.id, { [field]: value });
      toast.push("Campo actualizado");
      onChanged?.();
    } catch (e) {
      const msg = e?.response?.data?.error || "Error al guardar";
      toast.push(msg, "error");
      throw e;
    }
  }

  useEffect(() => {
    if (reassigning && allUsers.length === 0) {
      usersApi.list().then(r => setAllUsers(r.data));
    }
  }, [reassigning]);

  async function doReassign() {
    if (!chosen) return;
    try {
      await cases.reassign(c.id, Number(chosen));
      toast.push("Caso reasignado");
      setReassigning(false);
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al reasignar", "error");
    }
  }

  async function doFlagError() {
    try {
      await cases.flagError(c.id, flagMotivo);
      toast.push("Caso marcado por error");
      setConfirmFlag(false);
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error", "error");
    }
  }

  async function doSendBack() {
    const reason = sendBackReason.trim();
    if (reason.length < 10) {
      toast.push("La razón debe tener al menos 10 caracteres.", "error");
      return;
    }
    setSendBackBusy(true);
    try {
      await cases.adminSendBackToLawyer(c.id, reason);
      toast.push("Caso devuelto al abogado");
      setSendBackOpen(false);
      setSendBackReason("");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "No se pudo devolver el caso", "error");
    } finally {
      setSendBackBusy(false);
    }
  }

  async function doSoftDelete() {
    try {
      await cases.softDelete(c.id);
      toast.push("Caso eliminado");
      setConfirmSoftDelete(false);
      // Tell parent list to refresh, then close the panel.
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "No se pudo eliminar el caso", "error");
    }
  }

  // feature/directora-landing — directora resolve flow on a devuelto case.
  const [confirmResolve, setConfirmResolve] = useState(false);
  async function doResolveDevuelto() {
    try {
      await cases.action(c.id, "resolve_devuelto");
      toast.push("Caso resuelto. El abogado fue notificado.");
      setConfirmResolve(false);
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error", "error");
    }
  }

  // Hotfix — abogado/admin/directora can flip requiere_insumo from the case
  // detail. Optimistic UI; backend handles state-machine side-effects.
  // feature/single-role-audit — multi-role aware.
  const canToggleRequiereInsumo = isAbogadoAssigned || _gtRoles.includes("admin") || _gtRoles.includes("directora");
  async function toggleRequiereInsumo() {
    const next = !c.requiere_insumo;
    try {
      await cases.update(c.id, { requiere_insumo: next });
      toast.push(next
        ? "Caso enviado a Insumos."
        : "Caso ya no requiere insumos. Va directo al abogado.");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error", "error");
    }
  }

  return (
    <>
      {/* feature/carpetas-proceso — Phase 1. Renders only when the
          proceso already has ≥1 sibling carpeta. Sits at the very top
          so the carpetas team sees it before doing any work on the
          case. onOpenCase is intentionally omitted here — the parent
          slide-in panel doesn't expose a re-route handler, so the
          expanded list just shows case_number text. */}
      <ProcesoCarpetasBanner procesoCarpetas={c.proceso_carpetas} />

      {c.no_vincula_previsora && (
        <div className="warn-inline" style={{ marginBottom: 16, padding: 12, fontWeight: 600 }}>
          ⚠ Caso marcado: NO VINCULA A PREVISORA
        </div>
      )}

      {/* feature/insumo-recibido-tag. */}
      {c.insumo_recibido_notif_sent_at && (
        <div style={{
          marginBottom: 16, padding: "10px 14px",
          background: "var(--accent-mint-bg)",
          borderLeft: "3px solid var(--accent-mint)",
          borderRadius: 4, fontSize: 13,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>📩</span>
          <span>
            Insumo recibido el {fmt(c.insumo_recibido_notif_sent_at)}
            {c.lawyer_routed_fecha_envio && (
              <> · enviado el {String(c.lawyer_routed_fecha_envio).slice(0, 10)}</>
            )}
          </span>
        </div>
      )}

      {/* feature/impugnar-banner — quick-access impugnar banner for desfavorable fallos.
          feature/fallos-workflow-inversion — also visible in pendiente_revision_impugnacion.
          feature/desfavorable-parallel-decision. Ungated for
          desfavorables on any pipeline estado so Molly can decide
          impugnación in parallel with insumos team prep. Multi-role
          aware: accept admin OR revisor_fallos via user.roles[] (legacy
          users.role string is incomplete since Sprint 54b). */}
      {c.tipo === "fallo"
        && (c.sub_tipo === "primera_desfavorable" || c.sub_tipo === "segunda_desfavorable")
        && (
          c.estado === "fallo_pendiente_revision"
          || c.estado === "pendiente_revision_impugnacion"
          || ["asignado_abogado","pendiente_insumos","insumos_solicitados",
              "expediente_solicitado","insumos_recibidos"].includes(c.estado)
        )
        && ["revisor_fallos","admin"].some(r => _gtRoles.includes(r)) && (
        <div className="glass-section" style={{ marginBottom: 16, borderColor: "var(--accent-mint)" }}>
          <h3>Decisión de impugnación</h3>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            Este fallo es desfavorable. Tome la decisión rápida o use la pestaña Decisión para más contexto.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={async () => {
              try {
                await cases.action(c.id, "decide_impugnar", { impugnar: true });
                toast.push("Decisión: Impugnar registrada");
                onChanged?.();
              } catch (e) {
                toast.push(e?.response?.data?.error || "Error", "error");
              }
            }}>Impugnar</button>
            {/* Sprint X2 — opens CenteredModal (replaces window.prompt). */}
            <button className="secondary" onClick={() => setNoImpugnarOpen(true)}>
              No impugnar
            </button>
          </div>
        </div>
      )}

      {c.estado === "devuelto_a_reparto" && isDirectora && (
        <div className="card tight" style={{ marginBottom: 16, background: "var(--color-warning-bg)", borderColor: "var(--color-warning)" }}>
          <div style={{ fontWeight: 600, color: "var(--color-warning)", marginBottom: 4 }}>
            Caso devuelto por {c.abogado_nombre || "abogado"} — {fmt(c.updated_at)}
          </div>
          <div style={{ fontSize: 13, color: "var(--text-body)" }}>
            <strong>Motivo:</strong> {c.insumos_invalidos_motivo || "—"}
          </div>
          <div style={{ marginTop: 12, padding: 10, background: "var(--accent-mint-bg)", borderRadius: 8, fontSize: 12, color: "var(--text-body)" }}>
            Coordine con {c.abogado_nombre || "el abogado"} vía WhatsApp para obtener los insumos faltantes.
            Cuando estén cargados, marque como resuelto.
          </div>
          <div style={{ marginTop: 12 }}>
            <button onClick={() => setConfirmResolve(true)}>Marcar como resuelto y enviar al abogado</button>
          </div>
        </div>
      )}

      <div className="glass-section">
        <h3>Datos del caso</h3>
        <dl className="kvs">
          <dt>Número de caso</dt>   <dd>{c.case_number}</dd>
          <dt>Tipo de actuación</dt> <dd>
            {/* feature/tipo-fallo-edit — display + inline edit
                affordance. Editor modal applies cascade: sub_tipo
                + Sprint 58d requiere_insumo. Currently fallo-only
                (the legacy tipo_fallo enum is fallo-specific);
                other tipos surface plain text. */}
            <TipoActuacionDisplay c={c} onChanged={onChanged} />
          </dd>
          <dt>Radicado</dt>
          <dd>
            {isReparto
              ? <InlineEdit value={c.radicado} onSave={(v) => saveField("radicado", v)} />
              : c.radicado}
            {/* Sprint 73 change 3 — partial-radicado hint. When the
                case has a radicado but no proceso_id, the length-gate
                kept it un-grouped (short form < 15 digits, or
                non-digit corruption). The team needs to know it
                won't show up in the carpeta cross-case JOIN until
                the full radicado is captured. */}
            {c.radicado && !c.proceso_id && (
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                Radicado parcial — proceso no agrupado
              </div>
            )}
          </dd>
          <dt>Juzgado</dt>
          <dd>
            {isReparto
              ? <InlineEdit value={c.juzgado || ""} onSave={(v) => saveField("juzgado", v || null)} placeholder="(opcional)" />
              : (c.juzgado || "—")}
          </dd>
          <dt>Accionante</dt>       <dd>
            {/* feature/inline-accionante-edit — primary data-panel dd
                for the General tab. Subtitle + Decisión-tab dds stay
                plain per spec. */}
            <InlineEditAccionante c={c} onUpdated={onChanged} />
          </dd>
          <dt>Fecha de ingreso</dt> <dd>{fmt(c.fecha_ingreso)}</dd>
          <dt>Término</dt>
          <dd>
            {isReparto
              ? <InlineEdit
                  value={c.termino_horas != null ? String(c.termino_horas) : ""}
                  type="select"
                  options={[{ value: "", label: "—" }, ...TERMINO_OPTIONS.map(h => ({ value: String(h), label: formatTermino(h) }))]}
                  onSave={(v) => saveField("termino_horas", v ? Number(v) : null)}
                  renderValue={() => formatTermino(c.termino_horas)}
                />
              : formatTermino(c.termino_horas)}
          </dd>
          {/* feature/two-clock-architecture. Render extended
              deadlines only when moderation has overridden the default
              termino-based clock. Keeps the visual noise off baseline cases. */}
          {c.lawyer_deadline_at && (
            <>
              <dt>Plazo del abogado</dt>
              <dd>
                {fmt(c.lawyer_deadline_at)}{" "}
                <span className="badge mint" style={{ fontSize: 10 }}>Extendido por moderación</span>
              </dd>
            </>
          )}
          {c.insumos_deadline_at && (
            <>
              <dt>Plazo de insumos</dt>
              <dd>
                {fmt(c.insumos_deadline_at)}{" "}
                <span className="badge muted" style={{ fontSize: 10 }}>Override moderación</span>
              </dd>
            </>
          )}
          {/* feature/fallo-dropdown-dedup — fallo's tipo_fallo enum row removed; the
              "Tipo de actuación" row above already renders the hierarchical
              "Fallo primera instancia — Favorable" via formatTipo(). The
              raw enum (FAVORABLE_1A etc.) was confusing Reparto. The
              Decisión tab still shows tipo_fallo for Molly's workflow. */}
          {c.tipo === "desacato" && c.cierre_desacato && (<><dt>Cierre</dt><dd>{c.cierre_desacato}</dd></>)}
          {c.tipo === "sancion" && (<>
            <dt>Sanción</dt><dd>{c.sancion_tipo}</dd>
            {c.etapa_procesal && <><dt>Etapa procesal</dt><dd>{c.etapa_procesal}</dd></>}
            {c.fecha_cierre && <><dt>Fecha cierre</dt><dd>{c.fecha_cierre}</dd></>}
          </>)}
        </dl>
      </div>

      <div className="glass-section">
        <h3>Estado y asignación</h3>
        <dl className="kvs">
          <dt>Estado</dt>
          <dd>
            <EstadoPill c={c} />
            {/* feature/estado-prefer-subtipo. When the pill is
                showing the sub_tipo label (because the workflow estado is
                generic), surface the underlying workflow phase as muted
                helper text so the operational state stays visible. */}
            {c.sub_tipo && PREFER_SUB_TIPO_STATES.has(c.estado) && (
              <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>
                (fase del trámite: {ESTADO_LABEL[c.estado] || c.estado})
              </span>
            )}
          </dd>
          <dt>Abogado asignado</dt>
          <dd>
            {c.abogado_nombre || <span className="muted">Sin asignar</span>}
            {canAssignLawyer && (
              <button className="secondary sm" style={{ marginLeft: 8 }}
                      onClick={() => { setChosen(c.abogado_asignado_id || ""); setReassigning(true); }}>
                {c.abogado_asignado_id ? "Reasignar" : "Asignar abogado"}
              </button>
            )}
          </dd>
          <dt>Insumos asignado a</dt> <dd>{c.insumos_nombre || <span className="muted">—</span>}</dd>
          <dt>Banderas</dt>
          <dd>
            {c.flag_dp && <span className="badge info" style={{ marginRight: 4 }}>DP</span>}
            {c.flag_mp && <span className="badge info" style={{ marginRight: 4 }}>MP</span>}
            {c.flag_na && <span className="badge warning" style={{ marginRight: 4 }}>N/A {c.entidad_na ? `· ${c.entidad_na}` : ""}</span>}
            {c.forzar_insumo && <span className="badge danger" style={{ marginRight: 4 }}>Forzar insumo</span>}
            {/* feature/case-flags. */}
            {c.flag_derecho_peticion && <span className="badge mint" style={{ marginRight: 4 }}>Derecho de petición</span>}
            {c.flag_medida_provisional && <span className="badge mint" style={{ marginRight: 4 }}>Medida provisional</span>}
            {/* feature/nulidad-bandera — sprint86. Visible to all roles so
                anyone reading the case knows it's a post-nulidad re-intake. */}
            {c.flag_vuelve_despues_de_nulidad && <span className="badge warning" style={{ marginRight: 4 }}>Vuelve después de nulidad</span>}
            {c.sancion_no_previsora && <span className="badge muted" style={{ marginRight: 4 }}>No Previsora</span>}
            {c.insumo_marcado_deficiente_at && <span className="badge warning" style={{ marginRight: 4 }}>Insumo deficiente</span>}
            {!(c.flag_dp || c.flag_mp || c.flag_na || c.forzar_insumo
               || c.flag_derecho_peticion || c.flag_medida_provisional
               || c.flag_vuelve_despues_de_nulidad
               || c.sancion_no_previsora || c.insumo_marcado_deficiente_at) && <span className="muted">—</span>}
          </dd>
          {/* feature/case-flags. Triage roles can toggle the
              derecho_peticion + medida_provisional flags inline. */}
          {/* feature/single-role-audit — collapsed to a single multi-role
              check via rolesOf(). */}
          {(isReparto || ["insumos","reparto_insumos"].some(r => _gtRoles.includes(r))) && (
            <>
              <dt>Banderas adicionales</dt>
              <dd>
                <CaseFlagsToggles c={c} onChanged={onChanged} />
              </dd>
            </>
          )}
        </dl>
      </div>

      <div className="glass-section">
        <h3>Configuración</h3>
        <dl className="kvs">
          <dt>Requiere insumo</dt>
          <dd>
            {canToggleRequiereInsumo ? (
              <div>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
                  <input type="checkbox" checked={!!c.requiere_insumo} onChange={toggleRequiereInsumo} />
                  {c.requiere_insumo ? "Sí, requiere insumo" : "No requiere"}
                </label>
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  Marque si este caso necesita insumos adicionales antes de poder responder.
                </div>
              </div>
            ) : (
              <span className={c.requiere_insumo ? "" : "muted"}>{c.requiere_insumo ? "Sí" : "No"}</span>
            )}
          </dd>
          {/* feature/lawyer-agency-over-insumos. */}
          <dt>Acciones del abogado</dt>
          <dd>
            {/* sprint83.2 — mandatory LITISOFT dropdowns inline. Rendered
                above LawyerActionPanel so the lawyer sees + fills the
                gating fields right before the contestada button. */}
            <LitisoftMandatoryInline caseData={c} onSaved={onChanged} />
            <LawyerAgencyButtons c={c} user={user} onChanged={onChanged} />
            {/* feature/lifecycle-redesign. LawyerActionPanel
                supersedes the Sprint 50 MarcarContestada + Sprint 50
                SolicitarModeracion pair. It surfaces the Esperando-insumo
                button + Timer 1/2 banners + a centered-modal contestar
                flow. Self-gates per state and per role. */}
            <LawyerActionPanel caseData={c} user={user} onCaseUpdate={onChanged} />
            {/* feature/sancion-lifecycle. */}
            <SancionLifecycle c={c} user={user} onChanged={onChanged} />
            {/* feature/insumo-deficiente. */}
            <MarcarInsumoDeficiente c={c} user={user} onChanged={onChanged} />
          </dd>
          <dt>Observaciones</dt>
          <dd>
            {(isReparto || isAbogadoAssigned || isDirectora)
              ? <InlineEdit value={c.observaciones || ""} type="textarea"
                            onSave={(v) => saveField("observaciones", v || null)}
                            placeholder="Sin observaciones" />
              : (c.observaciones || <span className="muted">—</span>)}
          </dd>
          {/* feature/bot-litisoft-hechos — the LITISOFT-required narrative. An
              empty hechos leaves a matched case LITISOFT-incomplete (never
              auto-uploaded); filling it here is what promotes the case into the
              Bot LITISOFT cola de carga. Same affordance gate as the
              observaciones/radicado inline edits above (any admin/reparto, or the
              assigned abogado); the PUT enforces the real scope server-side —
              admin + reparto-OWN + assigned abogado. Directora is excluded there
              (hechos is not in her DIR allow-list), so she is not shown it. */}
          <dt>Hechos</dt>
          <dd>
            {(isReparto || isAbogadoAssigned)
              ? <InlineEdit value={c.hechos || ""} type="textarea"
                            onSave={(v) => saveField("hechos", v || null)}
                            placeholder="Sin hechos — requerido para LITISOFT" />
              : (c.hechos || <span className="muted">—</span>)}
          </dd>
        </dl>
      </div>

      {/* feature/carpetas-phase2-docs — P2 / P2.2 change 5.
          Uploaders (admin/carpetas/directora) see the full per-slot
          panel here. Lawyers see only the CarpetaStatusBadge in the
          modal header (above) — the per-slot detail would be noise
          since they don't manage carpeta documents. */}
      {(() => {
        const r = (user?.roles && user.roles.length) ? user.roles : (user?.role ? [user.role] : []);
        const canUpload = ["admin", "carpetas", "directora"].some(x => r.includes(x));
        return canUpload ? <CarpetaDocumentsPanel caseId={c.id} canUpload={true} /> : null;
      })()}

      {/* Late-entry banner + admin approval toggle (SPEC §11.3) */}
      {c.late_entry && (
        <div className="warn-inline" style={{ marginTop: 16 }}>
          <strong>Registro extemporáneo.</strong>{" "}
          {c.late_entry_approved
            ? "Aprobado — visible para los abogados."
            : "Pendiente de aprobación de administrador antes de ser visible para los abogados."}
          {c.fecha_real_notificacion && <> Fecha real de notificación: <strong>{c.fecha_real_notificacion}</strong>.</>}
          {/* feature/single-role-audit — multi-role aware. */}
          {_gtRoles.includes("admin") && !c.late_entry_approved && (
            <div style={{ marginTop: 8 }}>
              <button onClick={async () => {
                try { await cases.approveLateEntry(c.id); toast.push("Registro extemporáneo aprobado"); onChanged?.(); }
                catch (e) { toast.push(e?.response?.data?.error || "Error", "error"); }
              }}>Aprobar registro extemporáneo</button>
            </div>
          )}
        </div>
      )}

      {/* P2.5 — siniestro/Previsora metadata panel. Accessible to
          assigned abogado, admin, directora, carpetas via user.roles[]
          union. The carpetas team often backfills these fields when
          the abogado didn't have them at intake. */}
      {(() => {
        const r = (user?.roles && user.roles.length) ? user.roles : (user?.role ? [user.role] : []);
        const canEditSiniestro =
          r.includes("admin") || r.includes("directora") || r.includes("carpetas") || r.includes("reparto") ||
          r.includes("litisoft") ||  // feature/litisoft-role — sprint87
          (r.includes("abogado") && c.abogado_asignado_id === user.id);
        return canEditSiniestro && (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--border-hairline)" }}>
            <LitisoftEditor c={c} onSaved={onChanged} />
          </div>
        );
      })()}

      {/* feature/depth-and-delete — danger action group at bottom of General tab. Both buttons hidden if neither allowed. */}
      {(canSoftDelete || canFlagError || canSendBack) && (
        <div className="case-detail-actions">
          {canSendBack && (
            <button className="secondary" onClick={() => setSendBackOpen(true)}>
              ↩️  Devolver al abogado
            </button>
          )}
          {canSoftDelete && (
            <button className="secondary danger" onClick={() => setConfirmSoftDelete(true)}>
              🗑  Eliminar caso
            </button>
          )}
          {canFlagError && (
            <button className="secondary danger" onClick={() => setConfirmFlag(true)}>
              ⚠  Marcar como creado por error
            </button>
          )}
        </div>
      )}

      {/* feature/desfavorable-parallel-decision. CenteredModal
          for the "No impugnar" motivo (replaces window.prompt in the
          quick-access banner above). The proper Decisión tab still has
          its own radio + textarea — that flow is unchanged. */}
      <CenteredModal
        open={noImpugnarOpen}
        onClose={() => { if (!noImpugnarBusy) { setNoImpugnarOpen(false); setNoImpugnarReason(""); } }}
        title="Indicar motivo para no impugnar"
        subtitle={`Caso ${c.case_number}.`}
        size="md"
        footer={
          <>
            <button className="secondary" disabled={noImpugnarBusy}
                    onClick={() => { setNoImpugnarOpen(false); setNoImpugnarReason(""); }}>
              Cancelar
            </button>
            <button onClick={submitNoImpugnar}
                    disabled={noImpugnarBusy || !noImpugnarReason.trim()}>
              {noImpugnarBusy ? "Registrando…" : "Confirmar"}
            </button>
          </>
        }
      >
        <div className="col" style={{ gap: 10 }}>
          <div className="muted" style={{ fontSize: 12 }}>
            Esta decisión queda registrada en el caso y notifica al equipo de
            insumos para que detenga la preparación.
          </div>
          <label style={{ fontSize: 12, textTransform: "none", letterSpacing: 0 }}>
            Motivo (obligatorio)
            <textarea value={noImpugnarReason}
                      onChange={(e) => setNoImpugnarReason(e.target.value)}
                      placeholder="Ej: el fallo está bien fundamentado, no hay vía clara de revocación."
                      rows={4}
                      disabled={noImpugnarBusy}
                      style={{ width: "100%", marginTop: 4, resize: "vertical" }} />
          </label>
        </div>
      </CenteredModal>

      {/* feature/admin-send-back. */}
      <CenteredModal
        open={sendBackOpen}
        onClose={() => { if (!sendBackBusy) { setSendBackOpen(false); setSendBackReason(""); } }}
        title="Devolver caso al abogado"
        subtitle={`${c.case_number} — actualmente en ${c.estado === "archivado" ? "archivado" : "pendiente de archivar"}.`}
        size="md"
        footer={
          <>
            <button className="secondary" disabled={sendBackBusy}
                    onClick={() => { setSendBackOpen(false); setSendBackReason(""); }}>
              Cancelar
            </button>
            <button onClick={doSendBack}
                    disabled={sendBackBusy || sendBackReason.trim().length < 10}>
              {sendBackBusy ? "Devolviendo…" : "Devolver al abogado"}
            </button>
          </>
        }
      >
        <div className="col" style={{ gap: 12 }}>
          <div style={{ fontSize: 13, color: "var(--text-body)" }}>
            El caso volverá a estado <strong>{ESTADO_LABEL["asignado_abogado"]}</strong>.
            Se borrarán los marcadores de contestada y archivado, y el
            Timer 1 se reiniciará con un plazo de 24 horas.
            El abogado asignado recibirá una notificación con su razón.
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Razón (mínimo 10 caracteres):
          </div>
          <textarea
            value={sendBackReason}
            onChange={(e) => setSendBackReason(e.target.value)}
            placeholder="Ej: la abogada marcó contestada en el caso equivocado; debería ser el TUT-2026-0421."
            rows={4}
            disabled={sendBackBusy}
            style={{ width: "100%", resize: "vertical" }}
          />
          <div className="muted" style={{ fontSize: 11 }}>
            {sendBackReason.trim().length} / 10 mínimo
          </div>
        </div>
      </CenteredModal>

      {reassigning && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>Reasignar abogado</h3>
          <select value={chosen} onChange={(e) => setChosen(e.target.value)}>
            <option value="">— elegir abogado —</option>
            {/* feature/single-role-audit — FALSE POSITIVE: list filter on
                another user's PRIMARY role. Every active abogado today
                has primary role 'abogado' (verified via C1.6 audit).
                If admins are ever assigned as backup abogados, switch
                to the multi-role users.withRoles() endpoint. */}
            {allUsers.filter(u => u.role === "abogado" && u.active).map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
          <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
            <button className="secondary" onClick={() => setReassigning(false)}>Cancelar</button>
            <button onClick={doReassign} disabled={!chosen}>Reasignar</button>
          </div>
        </div>
      )}

      <Confirm
        open={confirmFlag}
        title="Marcar como creado por error"
        message={
          <span>
            Indique el motivo. El caso saldrá del flujo activo y quedará registrado en el historial de auditoría.
            <textarea style={{ marginTop: 8 }} value={flagMotivo} onChange={(e) => setFlagMotivo(e.target.value)} placeholder="Motivo" />
          </span>
        }
        onClose={() => { setConfirmFlag(false); setFlagMotivo(""); }}
        onConfirm={doFlagError}
        confirmText="Marcar"
        danger
      />

      <Confirm
        open={confirmResolve}
        title="Confirmar resolución"
        message="Los insumos faltantes deben estar cargados antes de continuar. ¿El caso está listo? El abogado será notificado y el reloj SLA se reanudará."
        onClose={() => setConfirmResolve(false)}
        onConfirm={doResolveDevuelto}
        confirmText="Confirmar"
      />

      <Confirm
        open={confirmSoftDelete}
        title="¿Eliminar este caso?"
        message="El caso será movido a la papelera. Podrá recuperarlo desde la sección de administración. ¿Continuar?"
        onClose={() => setConfirmSoftDelete(false)}
        onConfirm={doSoftDelete}
        confirmText="Eliminar"
        danger
      />
    </>
  );
}

// feature/litisoft-export — LITISOFT editor inside the General tab.
// sprint83 — extended with 3 new enum fields + showRequired mode that
// renders asterisks on every field + completion chip in the header.
function LitisoftEditor({ c, onSaved }) {
  const toast = useToast();
  // P2.5 → sprint83 — initial pulls all 13 siniestro fields from the
  // case row. accionado_previsora is tri-state (true/false/null) so it
  // isn't collapsed to "" like the strings.
  const initial = {
    fecha_siniestro:        c.fecha_siniestro ? String(c.fecha_siniestro).slice(0, 10) : "",
    placa:                  c.placa || "",
    numero_siniestro:       c.numero_siniestro || "",
    departamento:           c.departamento || "",
    municipio:              c.municipio || "",
    tematica:               c.tematica || "",
    poliza_soat:            c.poliza_soat || "",
    accionado_previsora:    c.accionado_previsora == null ? null : !!c.accionado_previsora,
    vinculados:             c.vinculados || "",
    vinculado_principal:    c.vinculado_principal || "",
    // feature/litisoft-completion — sprint87. Optional capture field.
    cc_vinculados:          c.cc_vinculados || "",
    pretension:             c.pretension || "",
    calidad_parte_previsora: c.calidad_parte_previsora || "",
    tipo_notificacion:      c.tipo_notificacion || "",
  };
  const [vals, setVals] = useState(initial);
  const [busy, setBusy] = useState(false);
  // Has at least one filled value? Open by default if anything is set.
  const hasAny = Object.entries(initial).some(([, v]) => v != null && String(v).length);
  async function save() {
    setBusy(true);
    try {
      await cases.update(c.id, vals);
      toast.push("Datos del siniestro guardados");
      onSaved?.();
    } catch (e) { toast.push(e?.response?.data?.error || e?.response?.data?.message || "Error", "error"); }
    finally { setBusy(false); }
  }
  return (
    <>
      <LitisoftFields values={vals} onChange={setVals} initialOpen={hasAny} showRequired />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <button className="secondary" disabled={busy} onClick={save}>
          {busy && <Spinner />} Guardar datos del siniestro
        </button>
      </div>
    </>
  );
}

// Insumos tab implementation lives in views/insumos/InsumosTab.js — it handles
// read-only / request form / status panel / response upload internally based on
// user role + case state.

// ===========================================================================
// Decisión tab — Revisor de Fallos (Molly) decides impugnar Y/N (SPEC §9.4)
// Read-only when the case has already left the pre-decision states.
// feature/fallos-workflow-inversion: editable in BOTH legacy
// fallo_pendiente_revision and new pendiente_revision_impugnacion.
// ===========================================================================
